import { Progress, type ProgressProps } from '@components/atoms/Progress'

import type { ComponentDoc, PropDocs } from '../types'

type ProgressOwnProps = Pick<ProgressProps, 'value' | 'size' | 'progressColor' | 'className' | 'aria-label' | 'ref'>

const props: PropDocs<ProgressOwnProps> = {
  value: { type: 'number | null', description: 'How far along it is, from 0 to 100.' },
  size: {
    type: "'default' | 'compact'",
    default: "'default'",
    description: 'Default is an 8px bar you colour yourself. Compact is a 4px primary bar.',
  },
  progressColor: { type: 'string', description: 'Tailwind background class for the filled part.' },
  className: { type: 'string', description: 'Classes for the track, such as its background colour.' },
  'aria-label': { type: 'string', description: 'Says what is progressing. Required when no visible label names it.' },
  ref: { type: 'Ref<HTMLDivElement>', description: 'Forwarded to the track.' },
}

const doc: ComponentDoc<ProgressProps> = {
  slug: 'progress',
  name: 'Progress',
  tier: 'atom',
  description: 'A bar showing how far through something is. Built on the Radix progress primitive.',
  importStatement: "import { Progress } from '@components/atoms/Progress'",
  sourcePath: 'app/frontend/components/atoms/Progress.tsx',
  component: Progress,
  props,
  examples: [
    {
      title: 'Default',
      note: 'Pass both colours. ProgressBar wraps this with green or traffic-light colours.',
      variants: [
        { value: 60, progressColor: 'tw:bg-green-600', className: 'tw:bg-gray-blue-100', 'aria-label': 'Default' },
      ],
    },
    {
      title: 'Compact',
      note: 'For progress inside a table row or under a button, next to a count like "3 of 10 called".',
      render: () => (
        <div className="tw:inline-flex tw:w-48 tw:flex-col tw:gap-1">
          <Progress size="compact" value={30} aria-label="Calling progress for Doorknock follow-up" />
          <span className="tw:text-right tw:text-xs tw:tabular-nums tw:text-gray-500">3 of 10 called</span>
        </div>
      ),
      code: `<Progress size="compact" value={30} aria-label={t('calling_progress_label', { name })} />
<span className="tw:text-right tw:text-xs tw:tabular-nums tw:text-gray-500">3 of 10 called</span>`,
    },
  ],
  usage: {
    do: [
      'Show the count as text beside a compact bar. The bar alone is hard to read exactly.',
      'Use compact where an 8px bar would crowd the row.',
    ],
    dont: ['Use it for an unknown wait. Use a spinner.'],
  },
  a11y: [
    'Radix renders role="progressbar" with aria-valuenow, so screen readers announce the percentage.',
    'Give it an aria-label naming what is progressing.',
  ],
}

export default doc
